import Ajv2020, { type ErrorObject } from "ajv/dist/2020";
import addFormats from "ajv-formats";
import schemaJson from "./site.schema.json";
import { validateNesting, type NestingIssue } from "./component-rules";
import { validateHref, type Href } from "./url";

export interface ValidationFailure {
  path: string;
  message: string;
}

export interface DocumentValidationResult {
  valid: boolean;
  errors: ValidationFailure[];
  nestingIssues: NestingIssue[];
  linkIssues: ValidationFailure[];
}

// strictTypes is off because JSON Schema 2020-12 conditional subschemas
// (if/then inside allOf) cannot declare a redundant "type": "object" for
// every branch without breaking generated-type inference.
const ajv = new Ajv2020({ allErrors: true, strict: true, strictTypes: false });
addFormats(ajv);
const compileSchema = ajv.compile(schemaJson);

function formatError(err: ErrorObject): ValidationFailure {
  return {
    path: err.instancePath || "/",
    message: err.message
      ? `${err.message}${err.params ? ` (${JSON.stringify(err.params)})` : ""}`
      : "is invalid",
  };
}

interface NodeLike {
  id: string;
  type: string;
  props?: Record<string, unknown>;
  children?: NodeLike[];
}

function collectHrefIssues(
  nodes: NodeLike[],
  pageIds: ReadonlySet<string>,
  seenPages: Set<string>,
  out: ValidationFailure[],
): void {
  for (const node of nodes) {
    const props = node.props ?? {};
    const check = (raw: unknown, field: string) => {
      if (raw && typeof raw === "object") {
        const href = raw as Href;
        const result = validateHref(href, { pageIds });
        if (!result.ok) {
          out.push({
            path: `/${node.id}/${field}`,
            message: result.reason ?? "invalid link",
          });
        }
        if (
          href.type === "page" &&
          typeof href.pageId === "string" &&
          pageIds.has(href.pageId)
        ) {
          seenPages.add(href.pageId);
        }
      }
    };
    check(props.href, "href");
    if (Array.isArray(props.links)) {
      for (const link of props.links) {
        check((link as { href?: unknown }).href, "links/href");
      }
    }
    if (Array.isArray(props.content)) {
      for (const run of props.content) {
        check((run as { link?: unknown }).link, "content/link");
      }
    }
    if (node.children?.length) {
      collectHrefIssues(node.children, pageIds, seenPages, out);
    }
  }
}

/**
 * Full document validation: structural (canonical JSON Schema),
 * nesting (component rules), and link integrity (PRD §59, §66, §182).
 */
export function validateDocument(doc: unknown): DocumentValidationResult {
  const valid = compileSchema(doc);
  const errors = valid ? [] : (compileSchema.errors ?? []).map(formatError);

  const nestingIssues: NestingIssue[] = [];
  const linkIssues: ValidationFailure[] = [];

  if (doc && typeof doc === "object") {
    const d = doc as {
      pages?: { nodes?: NodeLike[] }[];
      header?: NodeLike;
      footer?: NodeLike;
    };
    const pageIds = new Set(
      (d.pages ?? []).map((p, i) => (p as { id?: string }).id ?? `page-${i}`),
    );
    const seenPages = new Set<string>();

    if (d.header) {
      nestingIssues.push(...validateNesting([d.header], "Document"));
      collectHrefIssues([d.header], pageIds, seenPages, linkIssues);
    }
    if (d.footer) {
      nestingIssues.push(...validateNesting([d.footer], "Document"));
      collectHrefIssues([d.footer], pageIds, seenPages, linkIssues);
    }
    for (const page of d.pages ?? []) {
      nestingIssues.push(...validateNesting(page.nodes ?? [], "Page"));
      collectHrefIssues(page.nodes ?? [], pageIds, seenPages, linkIssues);
    }
  }

  return {
    valid: valid && nestingIssues.length === 0 && linkIssues.length === 0,
    errors,
    nestingIssues,
    linkIssues,
  };
}
